import type {
  RenderBackend,
  RenderBackendStatus,
  RenderFrameView,
  RenderSurfaceView,
} from '../RenderBackend.js'
import {
  motionBlurTapAlpha,
  motionBlurTapCount,
  motionBlurTapDistance,
  writeMotionBlurVector,
} from '../motionBlur.js'
import {
  CANONICAL_SHAPES,
  buildRenderPasses,
  resolveCustomSvgAspectFit,
  resolveFramePaint,
  writeParticleTransform,
  type MutableParticleTransform,
  type ResolvedFramePaint,
  type ResolvedCustomShape,
  type ResolvedShape,
  type ShapeCommand,
} from '../shapes/shapeGeometry.js'

const MAX_PATH_CACHE_ENTRIES = 32
const MAX_CUSTOM_CACHE_ENTRIES = 2
const LOCAL_COMPOSITE_SHADOW_PADDING_MULTIPLIER = 4
export const CUSTOM_SVG_CONTOUR_BUCKETS = Object.freeze([
  1, 2, 3, 4, 5, 6, 8, 10, 12, 16, 24, 32, 48, 64, 96, 128,
  192, 256, 384, 512,
] as const)
export const CUSTOM_SVG_MASK_SIZE = 128
const CUSTOM_SVG_DISTANCE_PADDING = 1
const CUSTOM_SVG_DISTANCE_SIZE = CUSTOM_SVG_MASK_SIZE + CUSTOM_SVG_DISTANCE_PADDING * 2
export const CUSTOM_SVG_CONTOUR_ATLAS_COLUMNS = 5
const CUSTOM_SVG_CONTOUR_ATLAS_ROWS = 4
export const CUSTOM_SVG_CONTOUR_GUTTER = 1
export const CUSTOM_SVG_CONTOUR_CELL_STRIDE = CUSTOM_SVG_MASK_SIZE + CUSTOM_SVG_CONTOUR_GUTTER * 2
export const CUSTOM_SVG_CONTOUR_ATLAS_WIDTH = CUSTOM_SVG_CONTOUR_CELL_STRIDE * CUSTOM_SVG_CONTOUR_ATLAS_COLUMNS
export const CUSTOM_SVG_CONTOUR_ATLAS_HEIGHT = CUSTOM_SVG_CONTOUR_CELL_STRIDE * CUSTOM_SVG_CONTOUR_ATLAS_ROWS
export const CUSTOM_SVG_RESOURCE_PIXEL_BYTES = (
  CUSTOM_SVG_MASK_SIZE * CUSTOM_SVG_MASK_SIZE
  + CUSTOM_SVG_CONTOUR_ATLAS_WIDTH * CUSTOM_SVG_CONTOUR_ATLAS_HEIGHT
) * 4
export const CANVAS_CUSTOM_CACHE_PIXEL_BYTES = CUSTOM_SVG_RESOURCE_PIXEL_BYTES * MAX_CUSTOM_CACHE_ENTRIES

export function quantizeCustomContourIndex(normalizedWidth: number): number {
  if (!Number.isFinite(normalizedWidth) || normalizedWidth <= 0) return 0
  const maximumBucket = CUSTOM_SVG_CONTOUR_BUCKETS[CUSTOM_SVG_CONTOUR_BUCKETS.length - 1] ?? CUSTOM_SVG_MASK_SIZE
  const requestedPixels = Math.max(1, Math.min(
    maximumBucket,
    normalizedWidth * CUSTOM_SVG_MASK_SIZE,
  ))
  if (requestedPixels > CUSTOM_SVG_MASK_SIZE) {
    for (let index = 0; index < CUSTOM_SVG_CONTOUR_BUCKETS.length; index += 1) {
      const candidate = CUSTOM_SVG_CONTOUR_BUCKETS[index]
      if (candidate !== undefined && candidate >= requestedPixels) return index
    }
    return CUSTOM_SVG_CONTOUR_BUCKETS.length - 1
  }
  const requested = Math.max(1, Math.min(
    maximumBucket,
    Math.round(requestedPixels),
  ))
  let closestIndex = 0
  let closestDistance = Math.abs(requested - (CUSTOM_SVG_CONTOUR_BUCKETS[0] ?? 1))
  for (let index = 1; index < CUSTOM_SVG_CONTOUR_BUCKETS.length; index += 1) {
    const candidate = CUSTOM_SVG_CONTOUR_BUCKETS[index]
    if (candidate === undefined) continue
    const distance = Math.abs(requested - candidate)
    if (distance < closestDistance) {
      closestIndex = index
      closestDistance = distance
    }
  }
  return closestIndex
}

export function quantizeCustomContourWidth(normalizedWidth: number): number {
  return CUSTOM_SVG_CONTOUR_BUCKETS[quantizeCustomContourIndex(normalizedWidth)] ?? 1
}

export function resolveCustomContourWidthNormalized(normalizedWidth: number): number {
  const maximumBucket = CUSTOM_SVG_CONTOUR_BUCKETS[CUSTOM_SVG_CONTOUR_BUCKETS.length - 1] ?? CUSTOM_SVG_MASK_SIZE
  const maximumNormalizedWidth = maximumBucket / CUSTOM_SVG_MASK_SIZE
  if (Number.isFinite(normalizedWidth) && normalizedWidth > maximumNormalizedWidth) return normalizedWidth
  return quantizeCustomContourWidth(normalizedWidth) / CUSTOM_SVG_MASK_SIZE
}

export function resolveCustomContourDestinationExtent(normalizedWidth: number): number {
  return 1 + resolveCustomContourWidthNormalized(normalizedWidth)
}

export function neutralizeCustomSvgRasterRgb(rgba: Uint8ClampedArray): void {
  const pixelCount = CUSTOM_SVG_MASK_SIZE * CUSTOM_SVG_MASK_SIZE
  if (rgba.length !== pixelCount * 4) {
    throw new RangeError('Custom SVG source must be an exact 128×128 RGBA raster')
  }
  for (let index = 0; index < pixelCount; index += 1) {
    rgba[index * 4] = 255
    rgba[index * 4 + 1] = 255
    rgba[index * 4 + 2] = 255
  }
}

export interface CanvasPathLike {
  moveTo(x: number, y: number): void
  lineTo(x: number, y: number): void
  bezierCurveTo(cp1x: number, cp1y: number, cp2x: number, cp2y: number, x: number, y: number): void
  arc(x: number, y: number, radius: number, startAngle?: number, endAngle?: number, counterclockwise?: boolean): void
  roundRect(x: number, y: number, width: number, height: number, radius: number): void
  closePath(): void
}

export interface Canvas2DContextLike {
  fillStyle: string | CanvasGradient | CanvasPattern
  strokeStyle: string | CanvasGradient | CanvasPattern
  shadowColor: string
  shadowBlur: number
  lineWidth: number
  lineCap: CanvasLineCap
  lineJoin: CanvasLineJoin
  globalAlpha: number
  globalCompositeOperation: GlobalCompositeOperation
  save(): void
  restore(): void
  setTransform(a: number, b: number, c: number, d: number, e: number, f: number): void
  clearRect(x: number, y: number, width: number, height: number): void
  fillRect(x: number, y: number, width: number, height: number): void
  fill(path: CanvasPathLike, fillRule?: CanvasFillRule): void
  stroke(path: CanvasPathLike): void
  drawImage(image: CanvasImageSource, dx: number, dy: number, dWidth: number, dHeight: number): void
  drawImage(
    image: CanvasImageSource,
    sx: number,
    sy: number,
    sWidth: number,
    sHeight: number,
    dx: number,
    dy: number,
    dWidth: number,
    dHeight: number,
  ): void
}

export interface CanvasLike {
  width: number
  height: number
  getContext(kind: string, options?: unknown): unknown
  addEventListener(type: string, listener: (event: Event) => void): void
  removeEventListener(type: string, listener: (event: Event) => void): void
}

function completeCanvasRenderTransaction(
  context: Canvas2DContextLike,
  renderFailed: boolean,
  renderError: unknown,
): void {
  let cleanupFailed = false
  let cleanupError: unknown
  try {
    context.globalCompositeOperation = 'source-over'
  } catch (error) {
    cleanupFailed = true
    cleanupError = error
  }
  try {
    context.globalAlpha = 1
  } catch (error) {
    if (!cleanupFailed) {
      cleanupFailed = true
      cleanupError = error
    }
  }
  try {
    context.shadowBlur = 0
  } catch (error) {
    if (!cleanupFailed) {
      cleanupFailed = true
      cleanupError = error
    }
  }
  try {
    context.restore()
  } catch (error) {
    if (!cleanupFailed) {
      cleanupFailed = true
      cleanupError = error
    }
  }
  if (renderFailed) throw renderError
  if (cleanupFailed) throw cleanupError
}

export interface DecodedCanvasSvg {
  prepareStyles(
    fillColor: readonly [number, number, number],
    borderColor: readonly [number, number, number],
  ): void
  drawFill(context: Canvas2DContextLike): void
  drawBorder(context: Canvas2DContextLike, contourWidthNormalized: number): void
  close(): void
}

export interface CanvasCompositeSurface {
  readonly image: CanvasImageSource
  readonly context: Canvas2DContextLike
  resize(width: number, height: number): void
  close(): void
}

export interface Canvas2DBackendDependencies {
  readonly context?: Canvas2DContextLike
  readonly createPath?: () => CanvasPathLike
  readonly decodeSvg?: (custom: ResolvedCustomShape) => Promise<DecodedCanvasSvg>
  readonly createCompositeSurface?: () => CanvasCompositeSurface
}

interface CustomCacheEntry {
  readonly key: string
  readonly resource: DecodedCanvasSvg
}

function rgb(color: readonly [number, number, number]): string {
  return `rgb(${color[0]},${color[1]},${color[2]})`
}

function rgba(color: readonly [number, number, number], alpha: number): string {
  return `rgba(${color[0]},${color[1]},${color[2]},${alpha})`
}

function makeDefaultPath(): CanvasPathLike {
  const PathConstructor = globalThis.Path2D
  if (typeof PathConstructor !== 'function') throw new Error('Path2D is unavailable')
  return new PathConstructor()
}

function createRasterSurface(width: number, height = width): OffscreenCanvas | HTMLCanvasElement {
  if (typeof globalThis.OffscreenCanvas === 'function') return new OffscreenCanvas(width, height)
  if (typeof globalThis.document === 'undefined') throw new Error('Raster canvas is unavailable')
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  return canvas
}

function createDefaultCompositeSurface(): CanvasCompositeSurface {
  const surface = createRasterSurface(1)
  const context = surface.getContext('2d', { alpha: true, desynchronized: true })
  if (context === null || !('setTransform' in context)) {
    surface.width = 1
    surface.height = 1
    throw new Error('Local composite context is unavailable')
  }
  let closed = false
  return {
    image: surface,
    context,
    resize: (width, height) => {
      if (closed) throw new Error('Local composite surface is disposed')
      if (surface.width !== width) surface.width = width
      if (surface.height !== height) surface.height = height
    },
    close: () => {
      if (closed) return
      closed = true
      surface.width = 1
      surface.height = 1
    },
  }
}

function transformDistances(distances: Float32Array, width: number, height: number): void {
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const index = y * width + x
      let distance = distances[index] ?? Number.POSITIVE_INFINITY
      if (x > 0) distance = Math.min(distance, (distances[index - 1] ?? distance) + 1)
      if (y > 0) distance = Math.min(distance, (distances[index - width] ?? distance) + 1)
      if (x > 0 && y > 0) distance = Math.min(distance, (distances[index - width - 1] ?? distance) + Math.SQRT2)
      if (x + 1 < width && y > 0) distance = Math.min(distance, (distances[index - width + 1] ?? distance) + Math.SQRT2)
      distances[index] = distance
    }
  }
  for (let y = height - 1; y >= 0; y -= 1) {
    for (let x = width - 1; x >= 0; x -= 1) {
      const index = y * width + x
      let distance = distances[index] ?? Number.POSITIVE_INFINITY
      if (x + 1 < width) distance = Math.min(distance, (distances[index + 1] ?? distance) + 1)
      if (y + 1 < height) distance = Math.min(distance, (distances[index + width] ?? distance) + 1)
      if (x + 1 < width && y + 1 < height) distance = Math.min(distance, (distances[index + width + 1] ?? distance) + Math.SQRT2)
      if (x > 0 && y + 1 < height) distance = Math.min(distance, (distances[index + width - 1] ?? distance) + Math.SQRT2)
      distances[index] = distance
    }
  }
}

function sampleSignedDistance(
  distances: Float32Array,
  width: number,
  x: number,
  y: number,
): number {
  const maximum = width - 1
  const clampedX = Math.max(0, Math.min(maximum, x))
  const clampedY = Math.max(0, Math.min(maximum, y))
  const x0 = Math.floor(clampedX)
  const y0 = Math.floor(clampedY)
  const x1 = Math.min(maximum, x0 + 1)
  const y1 = Math.min(maximum, y0 + 1)
  const fractionX = clampedX - x0
  const fractionY = clampedY - y0
  const top = (distances[y0 * width + x0] ?? 0) * (1 - fractionX)
    + (distances[y0 * width + x1] ?? 0) * fractionX
  const bottom = (distances[y1 * width + x0] ?? 0) * (1 - fractionX)
    + (distances[y1 * width + x1] ?? 0) * fractionX
  const sampled = top * (1 - fractionY) + bottom * fractionY
  const outsideX = x < 0 ? -x : x > maximum ? x - maximum : 0
  const outsideY = y < 0 ? -y : y > maximum ? y - maximum : 0
  if (outsideX === 0 && outsideY === 0) return sampled
  return -Math.hypot(outsideX, outsideY, Math.max(0, -sampled))
}

/** Builds the fixed neutral contour atlas from source alpha only. */
export function buildCustomContourAtlasRgba(sourceRgba: ArrayLike<number>): Uint8ClampedArray {
  const sourcePixelCount = CUSTOM_SVG_MASK_SIZE * CUSTOM_SVG_MASK_SIZE
  if (sourceRgba.length !== sourcePixelCount * 4) {
    throw new RangeError('Custom SVG source must be an exact 128×128 RGBA raster')
  }
  const atlas = new Uint8ClampedArray(
    CUSTOM_SVG_CONTOUR_ATLAS_WIDTH * CUSTOM_SVG_CONTOUR_ATLAS_HEIGHT * 4,
  )
  const distancePixelCount = CUSTOM_SVG_DISTANCE_SIZE * CUSTOM_SVG_DISTANCE_SIZE
  const coverage = new Uint8Array(distancePixelCount)
  const distanceToInside = new Float32Array(distancePixelCount)
  const distanceToOutside = new Float32Array(distancePixelCount)
  let insideCount = 0
  for (let y = 0; y < CUSTOM_SVG_MASK_SIZE; y += 1) {
    for (let x = 0; x < CUSTOM_SVG_MASK_SIZE; x += 1) {
      const sourceIndex = y * CUSTOM_SVG_MASK_SIZE + x
      const inside = (sourceRgba[sourceIndex * 4 + 3] ?? 0) > 0
      const distanceIndex = (y + CUSTOM_SVG_DISTANCE_PADDING) * CUSTOM_SVG_DISTANCE_SIZE
        + x + CUSTOM_SVG_DISTANCE_PADDING
      coverage[distanceIndex] = inside ? 1 : 0
      if (inside) insideCount += 1
    }
  }
  if (insideCount === 0) return atlas
  for (let index = 0; index < distancePixelCount; index += 1) {
    const inside = coverage[index] === 1
    distanceToInside[index] = inside ? 0 : Number.POSITIVE_INFINITY
    distanceToOutside[index] = inside ? Number.POSITIVE_INFINITY : 0
  }
  transformDistances(distanceToInside, CUSTOM_SVG_DISTANCE_SIZE, CUSTOM_SVG_DISTANCE_SIZE)
  transformDistances(distanceToOutside, CUSTOM_SVG_DISTANCE_SIZE, CUSTOM_SVG_DISTANCE_SIZE)
  for (let index = 0; index < distancePixelCount; index += 1) {
    distanceToInside[index] = coverage[index] === 1
      ? (distanceToOutside[index] ?? 0) - 0.5
      : -((distanceToInside[index] ?? 0) - 0.5)
  }

  for (let bucketIndex = 0; bucketIndex < CUSTOM_SVG_CONTOUR_BUCKETS.length; bucketIndex += 1) {
    const contourWidth = CUSTOM_SVG_CONTOUR_BUCKETS[bucketIndex] ?? 1
    const contourWidthNormalized = contourWidth / CUSTOM_SVG_MASK_SIZE
    const destinationExtent = 1 + contourWidthNormalized
    const cellX = bucketIndex % CUSTOM_SVG_CONTOUR_ATLAS_COLUMNS
    const cellY = Math.floor(bucketIndex / CUSTOM_SVG_CONTOUR_ATLAS_COLUMNS)
    for (let localY = 0; localY < CUSTOM_SVG_MASK_SIZE; localY += 1) {
      const canonicalY = ((localY + 0.5) / CUSTOM_SVG_MASK_SIZE - 0.5) * destinationExtent
      const sampleY = (canonicalY + 0.5) * CUSTOM_SVG_MASK_SIZE + 0.5
      const atlasY = cellY * CUSTOM_SVG_CONTOUR_CELL_STRIDE + CUSTOM_SVG_CONTOUR_GUTTER + localY
      for (let localX = 0; localX < CUSTOM_SVG_MASK_SIZE; localX += 1) {
        const canonicalX = ((localX + 0.5) / CUSTOM_SVG_MASK_SIZE - 0.5) * destinationExtent
        const sampleX = (canonicalX + 0.5) * CUSTOM_SVG_MASK_SIZE + 0.5
        const signedDistance = sampleSignedDistance(
          distanceToInside,
          CUSTOM_SVG_DISTANCE_SIZE,
          sampleX,
          sampleY,
        )
        if (Math.abs(signedDistance) > contourWidth * 0.5) continue
        const atlasX = cellX * CUSTOM_SVG_CONTOUR_CELL_STRIDE + CUSTOM_SVG_CONTOUR_GUTTER + localX
        const outputOffset = (atlasY * CUSTOM_SVG_CONTOUR_ATLAS_WIDTH + atlasX) * 4
        atlas[outputOffset] = 255
        atlas[outputOffset + 1] = 255
        atlas[outputOffset + 2] = 255
        atlas[outputOffset + 3] = 255
      }
    }
  }
  return atlas
}

async function defaultDecodeSvg(custom: ResolvedCustomShape): Promise<DecodedCanvasSvg> {
  if (typeof globalThis.fetch !== 'function' || typeof globalThis.createImageBitmap !== 'function') {
    throw new Error('ImageBitmap decoding is unavailable')
  }
  const response = await globalThis.fetch(custom.dataUrl)
  if (!response.ok) throw new Error(`SVG decode failed with ${String(response.status)}`)
  const bitmap = await globalThis.createImageBitmap(await response.blob(), {
    premultiplyAlpha: 'premultiply',
    colorSpaceConversion: 'none',
  })
  const base = createRasterSurface(CUSTOM_SVG_MASK_SIZE)
  const baseContext = base.getContext('2d', { willReadFrequently: true })
  if (baseContext === null || !('getImageData' in baseContext)) {
    bitmap.close()
    throw new Error('Custom SVG raster context is unavailable')
  }
  const fit = resolveCustomSvgAspectFit(custom, CUSTOM_SVG_MASK_SIZE, CUSTOM_SVG_MASK_SIZE)
  const [destinationX, destinationY, width, height] = fit.destination
  baseContext.clearRect(0, 0, CUSTOM_SVG_MASK_SIZE, CUSTOM_SVG_MASK_SIZE)
  baseContext.drawImage(bitmap, destinationX, destinationY, width, height)
  bitmap.close()
  const source = baseContext.getImageData(0, 0, CUSTOM_SVG_MASK_SIZE, CUSTOM_SVG_MASK_SIZE)
  const contourRgba = buildCustomContourAtlasRgba(source.data)
  neutralizeCustomSvgRasterRgb(source.data)
  const fillSurface = createRasterSurface(CUSTOM_SVG_MASK_SIZE)
  const fillContext = fillSurface.getContext('2d')
  const contourAtlas = createRasterSurface(CUSTOM_SVG_CONTOUR_ATLAS_WIDTH, CUSTOM_SVG_CONTOUR_ATLAS_HEIGHT)
  const contourContext = contourAtlas.getContext('2d')
  if (fillContext === null || !('putImageData' in fillContext)
    || contourContext === null || !('putImageData' in contourContext)) {
    base.width = 1
    base.height = 1
    fillSurface.width = 1
    fillSurface.height = 1
    contourAtlas.width = 1
    contourAtlas.height = 1
    throw new Error('Custom SVG stamp context is unavailable')
  }
  fillContext.putImageData(source, 0, 0)
  const contourPixels = contourContext.createImageData(
    CUSTOM_SVG_CONTOUR_ATLAS_WIDTH,
    CUSTOM_SVG_CONTOUR_ATLAS_HEIGHT,
  )
  contourPixels.data.set(contourRgba)
  contourContext.putImageData(contourPixels, 0, 0)
  base.width = 1
  base.height = 1
  let fillRed = -1
  let fillGreen = -1
  let fillBlue = -1
  let borderRed = -1
  let borderGreen = -1
  let borderBlue = -1
  let closed = false
  const prepareStyles = (
    fillColor: readonly [number, number, number],
    borderColor: readonly [number, number, number],
  ): void => {
    if (closed) throw new Error('Custom SVG mask is disposed')
    if (fillRed !== fillColor[0] || fillGreen !== fillColor[1] || fillBlue !== fillColor[2]) {
      fillContext.save()
      fillContext.globalCompositeOperation = 'source-in'
      fillContext.fillStyle = `rgb(${fillColor[0]},${fillColor[1]},${fillColor[2]})`
      fillContext.fillRect(0, 0, CUSTOM_SVG_MASK_SIZE, CUSTOM_SVG_MASK_SIZE)
      fillContext.restore()
      fillRed = fillColor[0]
      fillGreen = fillColor[1]
      fillBlue = fillColor[2]
    }
    if (borderRed !== borderColor[0] || borderGreen !== borderColor[1] || borderBlue !== borderColor[2]) {
      contourContext.save()
      contourContext.globalCompositeOperation = 'source-in'
      contourContext.fillStyle = `rgb(${borderColor[0]},${borderColor[1]},${borderColor[2]})`
      contourContext.fillRect(0, 0, CUSTOM_SVG_CONTOUR_ATLAS_WIDTH, CUSTOM_SVG_CONTOUR_ATLAS_HEIGHT)
      contourContext.restore()
      borderRed = borderColor[0]
      borderGreen = borderColor[1]
      borderBlue = borderColor[2]
    }
  }
  return {
    prepareStyles,
    drawFill: (context) => {
      if (closed) return
      context.drawImage(fillSurface, -0.5, -0.5, 1, 1)
    },
    drawBorder: (context, contourWidthNormalized) => {
      if (closed) return
      const bucketIndex = quantizeCustomContourIndex(contourWidthNormalized)
      const cellX = bucketIndex % CUSTOM_SVG_CONTOUR_ATLAS_COLUMNS
      const cellY = Math.floor(bucketIndex / CUSTOM_SVG_CONTOUR_ATLAS_COLUMNS)
      const destinationExtent = resolveCustomContourDestinationExtent(contourWidthNormalized)
      context.drawImage(
        contourAtlas,
        cellX * CUSTOM_SVG_CONTOUR_CELL_STRIDE + CUSTOM_SVG_CONTOUR_GUTTER,
        cellY * CUSTOM_SVG_CONTOUR_CELL_STRIDE + CUSTOM_SVG_CONTOUR_GUTTER,
        CUSTOM_SVG_MASK_SIZE,
        CUSTOM_SVG_MASK_SIZE,
        destinationExtent * -0.5,
        destinationExtent * -0.5,
        destinationExtent,
        destinationExtent,
      )
    },
    close: () => {
      if (closed) return
      closed = true
      fillSurface.width = 1
      fillSurface.height = 1
      contourAtlas.width = 1
      contourAtlas.height = 1
    },
  }
}

function addCapsule(path: CanvasPathLike, command: Extract<ShapeCommand, { kind: 'centerline' }>): void {
  const angle = Math.atan2(command.y2 - command.y1, command.x2 - command.x1)
  const radius = command.width * 0.5
  const normalX = Math.cos(angle + Math.PI / 2) * radius
  const normalY = Math.sin(angle + Math.PI / 2) * radius
  path.moveTo(command.x1 + normalX, command.y1 + normalY)
  path.lineTo(command.x2 + normalX, command.y2 + normalY)
  path.arc(command.x2, command.y2, radius, angle + Math.PI / 2, angle - Math.PI / 2)
  path.lineTo(command.x1 - normalX, command.y1 - normalY)
  path.arc(command.x1, command.y1, radius, angle - Math.PI / 2, angle + Math.PI / 2)
  path.closePath()
}

function appendCommand(path: CanvasPathLike, command: ShapeCommand): void {
  switch (command.kind) {
    case 'move': path.moveTo(command.x, command.y); break
    case 'line': path.lineTo(command.x, command.y); break
    case 'cubic': path.bezierCurveTo(command.cp1x, command.cp1y, command.cp2x, command.cp2y, command.x, command.y); break
    case 'circle':
      path.moveTo(command.x + command.radius, command.y)
      path.arc(command.x, command.y, command.radius, 0, Math.PI * 2)
      path.closePath()
      break
    case 'arc':
      path.arc(
        command.x,
        command.y,
        command.radius,
        command.startAngle,
        command.endAngle,
        command.counterclockwise,
      )
      break
    case 'centerline': addCapsule(path, command); break
    case 'close': path.closePath(); break
  }
}

const FALLBACK_CIRCLE_SHAPE: ResolvedShape = Object.freeze({
  kind: 'built-in',
  stableKey: 'built-in:circle:0',
  builtIn: CANONICAL_SHAPES.circle,
  squareRadius: 0,
})

function localCompositeShapeExtent(paint: ResolvedFramePaint): number {
  if (paint.shape.kind === 'custom-svg') return 0.5
  const bounds = paint.shape.builtIn.bounds
  return Math.max(
    Math.abs(bounds[0]),
    Math.abs(bounds[1]),
    Math.abs(bounds[2]),
    Math.abs(bounds[3]),
  )
}

export class Canvas2DBackend implements RenderBackend {
  readonly kind = 'canvas2d' as const
  private status: RenderBackendStatus = 'ready'
  private readonly context: Canvas2DContextLike
  private readonly createPath: () => CanvasPathLike
  private readonly decodeSvg: (custom: ResolvedCustomShape) => Promise<DecodedCanvasSvg>
  private readonly createCompositeSurface: () => CanvasCompositeSurface
  private readonly statusListeners = new Set<(status: RenderBackendStatus) => void>()
  private readonly invalidationListeners = new Set<() => void>()
  private readonly pathCache = new Map<string, CanvasPathLike>()
  private readonly customCache = new Map<string, CustomCacheEntry>()
  private localCompositeSurface: CanvasCompositeSurface | null = null
  private localCompositeWidth = 0
  private localCompositeHeight = 0
  private readonly particleTransform: MutableParticleTransform = {
    displaySize: 0,
    centerX: 0,
    centerY: 0,
    scaleX: 0,
    scaleY: 0,
    rotation: 0,
    borderWidthCss: 0,
  }
  private readonly motionVector = { x: 0, y: 0 }
  private activeCustomKey = ''
  private pendingCustomKey = ''
  private decodeRevision = 0
  private surfaceWidth = 1
  private surfaceHeight = 1
  private surfaceDpr = 1

  constructor(
    private readonly canvas: CanvasLike,
    dependencies: Canvas2DBackendDependencies = {},
  ) {
    const context = dependencies.context ?? canvas.getContext('2d', { alpha: true, desynchronized: true })
    if (context === null || context === undefined) throw new Error('Canvas2D context is unavailable')
    this.context = context as Canvas2DContextLike
    this.createPath = dependencies.createPath ?? makeDefaultPath
    this.decodeSvg = dependencies.decodeSvg ?? defaultDecodeSvg
    this.createCompositeSurface = dependencies.createCompositeSurface ?? createDefaultCompositeSurface
  }

  getStatus(): RenderBackendStatus { return this.status }

  subscribeStatus(listener: (status: RenderBackendStatus) => void): () => void {
    if (typeof listener !== 'function') throw new TypeError('Status listener must be a function')
    this.statusListeners.add(listener)
    let active = true
    return () => {
      if (!active) return
      active = false
      this.statusListeners.delete(listener)
    }
  }

  subscribeInvalidation(listener: () => void): () => void {
    if (typeof listener !== 'function') throw new TypeError('Invalidation listener must be a function')
    this.invalidationListeners.add(listener)
    let active = true
    return () => {
      if (!active) return
      active = false
      this.invalidationListeners.delete(listener)
    }
  }

  resize(surface: Readonly<RenderSurfaceView>): void {
    if (this.status === 'disposed') return
    const width = Math.max(1, Math.round(surface.width * surface.effectiveDpr))
    const height = Math.max(1, Math.round(surface.height * surface.effectiveDpr))
    if (this.canvas.width !== width) this.canvas.width = width
    if (this.canvas.height !== height) this.canvas.height = height
    this.surfaceWidth = surface.width
    this.surfaceHeight = surface.height
    this.surfaceDpr = surface.effectiveDpr
  }

  render(frame: RenderFrameView): void {
    if (this.status !== 'ready') return
    const context = this.context
    const paint = resolveFramePaint(frame.project, frame.playback)
    const loadedCustom = this.customResource(paint.shape)
    let path: CanvasPathLike | null
    let custom: DecodedCanvasSvg | null
    if (paint.shape.kind === 'custom-svg') {
      custom = loadedCustom
      path = loadedCustom === null ? this.pathFor(FALLBACK_CIRCLE_SHAPE) : null
    } else {
      custom = null
      path = this.pathFor(paint.shape)
    }
    const dpr = frame.surface.effectiveDpr
    const pixelWidth = Math.max(1, Math.round(frame.surface.width * dpr))
    const pixelHeight = Math.max(1, Math.round(frame.surface.height * dpr))
    const fillStyle = rgb(paint.fillColor)
    const borderStyle = rgb(paint.borderColor)
    const glowStyle = rgba(paint.glowColor, paint.glowIntensity)
    if (custom !== null) custom.prepareStyles(paint.fillColor, paint.borderColor)

    let renderFailed = false
    let renderError: unknown
    context.save()
    try {
      context.globalCompositeOperation = 'source-over'
      context.globalAlpha = 1
      context.shadowBlur = 0
      context.setTransform(1, 0, 0, 1, 0, 0)
      if (paint.showBackground) {
        context.fillStyle = rgb(paint.backgroundColor)
        context.fillRect(0, 0, pixelWidth, pixelHeight)
      } else context.clearRect(0, 0, pixelWidth, pixelHeight)

      const passes = buildRenderPasses(paint)
      const blurTaps = motionBlurTapCount(frame)
      for (let tap = blurTaps; tap >= 1; tap -= 1) {
        const distance = motionBlurTapDistance(tap, blurTaps, frame.project.animation.motionBlur)
        const alpha = motionBlurTapAlpha(tap, blurTaps, frame.project.animation.motionBlur)
        for (let passIndex = 0; passIndex < passes.length; passIndex += 1) {
          const pass = passes[passIndex]
          if (pass !== undefined) {
            this.renderPass(frame, paint, path, custom, pass.kind, fillStyle, borderStyle, glowStyle, distance, alpha)
          }
        }
      }
      for (let passIndex = 0; passIndex < passes.length; passIndex += 1) {
        const pass = passes[passIndex]
        if (pass !== undefined) {
          this.renderPass(frame, paint, path, custom, pass.kind, fillStyle, borderStyle, glowStyle, 0, 1)
        }
      }
    } catch (error) {
      renderFailed = true
      renderError = error
    } finally {
      completeCanvasRenderTransaction(context, renderFailed, renderError)
    }
  }

  dispose(): void {
    if (this.status === 'disposed') return
    this.status = 'disposed'
    this.decodeRevision += 1
    for (const entry of this.customCache.values()) entry.resource.close()
    this.customCache.clear()
    this.pathCache.clear()
    this.localCompositeSurface?.close()
    this.localCompositeSurface = null
    this.localCompositeWidth = 0
    this.localCompositeHeight = 0
    this.statusListeners.clear()
    this.invalidationListeners.clear()
  }

  getDebugSnapshot(): Readonly<{
    pathCacheEntries: number
    customCacheEntries: number
    surfaceWidth: number
    surfaceHeight: number
    surfaceDpr: number
    customRetainedPixelBytes: number
    particleObjectAllocations: 0
    customVariantCacheEntries: 0
    localCompositeSurfaceCount: number
    localCompositePixelBytes: number
    localCompositeMetadataBytes: number
  }> {
    return {
      pathCacheEntries: this.pathCache.size,
      customCacheEntries: this.customCache.size,
      surfaceWidth: this.surfaceWidth,
      surfaceHeight: this.surfaceHeight,
      surfaceDpr: this.surfaceDpr,
      customRetainedPixelBytes: this.customCache.size * CUSTOM_SVG_RESOURCE_PIXEL_BYTES,
      particleObjectAllocations: 0,
      customVariantCacheEntries: 0,
      localCompositeSurfaceCount: this.localCompositeSurface === null ? 0 : 1,
      localCompositePixelBytes: this.localCompositeWidth * this.localCompositeHeight * 4,
      localCompositeMetadataBytes: 0,
    }
  }

  private emitInvalidation(): void {
    for (const listener of this.invalidationListeners) {
      try { listener() } catch { /* Resource observers cannot invalidate cache ownership. */ }
    }
  }

  private pathFor(shape: ResolvedShape): CanvasPathLike {
    const cached = this.pathCache.get(shape.stableKey)
    if (cached !== undefined) {
      this.pathCache.delete(shape.stableKey)
      this.pathCache.set(shape.stableKey, cached)
      return cached
    }
    const path = this.createPath()
    if (shape.builtIn.id === 'square' && shape.squareRadius > 0) {
      path.roundRect(-0.5, -0.5, 1, 1, shape.squareRadius)
    } else {
      for (const command of shape.builtIn.commands) appendCommand(path, command)
    }
    this.pathCache.set(shape.stableKey, path)
    if (this.pathCache.size > MAX_PATH_CACHE_ENTRIES) {
      const oldest = this.pathCache.keys().next().value
      if (oldest !== undefined) this.pathCache.delete(oldest)
    }
    return path
  }

  private customResource(shape: ResolvedShape): DecodedCanvasSvg | null {
    const custom = shape.custom
    if (custom === undefined) {
      if (this.activeCustomKey !== '') {
        this.activeCustomKey = ''
        this.pendingCustomKey = ''
        this.decodeRevision += 1
      }
      return null
    }
    if (this.activeCustomKey !== shape.stableKey) {
      this.activeCustomKey = shape.stableKey
      this.pendingCustomKey = ''
      this.decodeRevision += 1
    }
    const cached = this.customCache.get(shape.stableKey)
    if (cached !== undefined) {
      this.customCache.delete(shape.stableKey)
      this.customCache.set(shape.stableKey, cached)
      return cached.resource
    }
    if (this.pendingCustomKey === shape.stableKey) return null
    this.pendingCustomKey = shape.stableKey
    const revision = this.decodeRevision
    void this.decodeSvg(custom).then((resource) => {
      if (this.status !== 'ready' || revision !== this.decodeRevision || this.activeCustomKey !== shape.stableKey) {
        resource.close()
        return
      }
      this.pendingCustomKey = ''
      this.customCache.set(shape.stableKey, { key: shape.stableKey, resource })
      while (this.customCache.size > MAX_CUSTOM_CACHE_ENTRIES) {
        const oldest = this.customCache.keys().next().value
        if (oldest === undefined) break
        const evicted = this.customCache.get(oldest)
        this.customCache.delete(oldest)
        evicted?.resource.close()
      }
      this.emitInvalidation()
    }).catch(() => {
      if (revision === this.decodeRevision) this.pendingCustomKey = ''
    })
    return null
  }

  private ensureLocalCompositeSurface(
    requiredWidth: number,
    requiredHeight: number,
    outputWidth: number,
    outputHeight: number,
  ): CanvasCompositeSurface | null {
    let surface = this.localCompositeSurface
    if (surface === null) {
      if (requiredWidth <= 0 || requiredHeight <= 0) return null
      surface = this.createCompositeSurface()
      this.localCompositeSurface = surface
    }
    const width = Math.min(outputWidth, Math.max(
      requiredWidth,
      Math.min(this.localCompositeWidth, outputWidth),
    ))
    const height = Math.min(outputHeight, Math.max(
      requiredHeight,
      Math.min(this.localCompositeHeight, outputHeight),
    ))
    if (width !== this.localCompositeWidth || height !== this.localCompositeHeight) {
      surface.resize(width, height)
      this.localCompositeWidth = width
      this.localCompositeHeight = height
    }
    return surface
  }

  private renderPass(
    frame: RenderFrameView,
    paint: ResolvedFramePaint,
    path: CanvasPathLike | null,
    custom: DecodedCanvasSvg | null,
    pass: 'local-composite' | 'glow-fill' | 'border',
    fillStyle: string,
    borderStyle: string,
    glowStyle: string,
    motionDistance: number,
    motionAlpha: number,
  ): void {
    const context = this.context
    const glowEnabled = paint.showGlow && paint.glowIntensity > 0
    const glowBlur = glowEnabled ? paint.glowSoftness * frame.surface.effectiveDpr : 0
    const borderEnabled = paint.showBorder && paint.borderIntensity > 0
    if (pass === 'border' && !borderEnabled) return
    if (pass === 'local-composite' && glowEnabled && borderEnabled) {
      context.strokeStyle = borderStyle
      context.lineCap = 'round'
      context.lineJoin = 'round'
      this.renderIsolatedLocalComposite(
        frame, paint, path, custom, fillStyle, glowStyle, glowBlur, motionDistance, motionAlpha,
      )
      return
    }
    if (pass !== 'border') {
      context.fillStyle = fillStyle
      if (glowEnabled) context.shadowColor = glowStyle
      context.shadowBlur = glowBlur
    }
    if (pass !== 'glow-fill' && borderEnabled) {
      context.strokeStyle = borderStyle
      context.lineCap = 'round'
      context.lineJoin = 'round'
      if (pass === 'border') context.shadowBlur = 0
    }
    const particles = frame.particles
    const renderCount = Math.min(particles.renderCount, 100_000)
    for (let visibleIndex = 0; visibleIndex < renderCount; visibleIndex += 1) {
      const particleIndex = particles.indices[visibleIndex]
      if (particleIndex === undefined || particleIndex >= particles.sourceCount) continue
      const size = particles.size[particleIndex]
      const x = particles.positionX[particleIndex]
      const y = particles.positionY[particleIndex]
      const rotation = particles.rotation[particleIndex]
      const particleAlpha = particles.alpha[particleIndex]
      if (size === undefined || x === undefined || y === undefined || rotation === undefined || particleAlpha === undefined) continue
      if (!Number.isFinite(size) || !Number.isFinite(x) || !Number.isFinite(y)
        || !Number.isFinite(rotation) || !Number.isFinite(particleAlpha)) continue
      let sampledX = x
      let sampledY = y
      if (motionDistance > 0) {
        if (!writeMotionBlurVector(
          this.motionVector,
          x,
          y,
          particles.previousPositionX?.[particleIndex],
          particles.previousPositionY?.[particleIndex],
          frame.viewport.zoom,
        )) continue
        sampledX += this.motionVector.x * motionDistance
        sampledY += this.motionVector.y * motionDistance
      }
      const finalAlpha = Math.max(0, Math.min(
        1,
        particleAlpha * paint.fadeAlpha * (motionDistance > 0 ? motionAlpha : 1),
      ))
      if (finalAlpha <= 0 || size <= 0) continue
      if (pass === 'border') this.drawBorder(frame, paint, path, custom, sampledX, sampledY, size, rotation, finalAlpha)
      else if (pass === 'glow-fill') this.drawGlowFill(frame, paint, path, custom, sampledX, sampledY, size, rotation, finalAlpha)
      else {
        this.drawGlowFill(frame, paint, path, custom, sampledX, sampledY, size, rotation, finalAlpha)
        if (borderEnabled) {
          if (glowEnabled) context.shadowBlur = 0
          this.drawBorder(frame, paint, path, custom, sampledX, sampledY, size, rotation, finalAlpha)
          if (glowEnabled) context.shadowBlur = glowBlur
        }
      }
    }
  }

  private renderIsolatedLocalComposite(
    frame: RenderFrameView,
    paint: ResolvedFramePaint,
    path: CanvasPathLike | null,
    custom: DecodedCanvasSvg | null,
    fillStyle: string,
    glowStyle: string,
    glowBlur: number,
    motionDistance: number,
    motionAlpha: number,
  ): void {
    const particles = frame.particles
    const outputWidth = Math.max(1, Math.round(frame.surface.width * frame.surface.effectiveDpr))
    const outputHeight = Math.max(1, Math.round(frame.surface.height * frame.surface.effectiveDpr))
    const shapeExtent = localCompositeShapeExtent(paint)
    // Canvas does not expose its blur kernel radius. Four blur radii plus an
    // antialias guard conservatively bounds the pixels replayed per particle.
    const glowPadding = Math.ceil(
      paint.glowSoftness * frame.surface.effectiveDpr * LOCAL_COMPOSITE_SHADOW_PADDING_MULTIPLIER + 2,
    )
    const renderCount = Math.min(particles.renderCount, 100_000)
    let requiredWidth = 0
    let requiredHeight = 0

    // Size the reusable buffer once per pass. It grows to the largest clipped
    // particle and only shrinks when the output itself becomes smaller.
    for (let visibleIndex = 0; visibleIndex < renderCount; visibleIndex += 1) {
      const particleIndex = particles.indices[visibleIndex]
      if (particleIndex === undefined || particleIndex >= particles.sourceCount) continue
      const size = particles.size[particleIndex]
      const x = particles.positionX[particleIndex]
      const y = particles.positionY[particleIndex]
      const rotation = particles.rotation[particleIndex]
      const particleAlpha = particles.alpha[particleIndex]
      if (size === undefined || x === undefined || y === undefined || rotation === undefined || particleAlpha === undefined) continue
      if (!Number.isFinite(size) || !Number.isFinite(x) || !Number.isFinite(y)
        || !Number.isFinite(rotation) || !Number.isFinite(particleAlpha)) continue
      let sampledX = x
      let sampledY = y
      if (motionDistance > 0) {
        if (!writeMotionBlurVector(
          this.motionVector,
          x,
          y,
          particles.previousPositionX?.[particleIndex],
          particles.previousPositionY?.[particleIndex],
          frame.viewport.zoom,
        )) continue
        sampledX += this.motionVector.x * motionDistance
        sampledY += this.motionVector.y * motionDistance
      }
      const finalAlpha = Math.max(0, Math.min(
        1,
        particleAlpha * paint.fadeAlpha * (motionDistance > 0 ? motionAlpha : 1),
      ))
      if (finalAlpha <= 0 || size <= 0) continue

      const transform = writeParticleTransform(
        this.particleTransform,
        sampledX,
        sampledY,
        size,
        rotation,
        frame.project,
        frame.viewport,
        frame.surface.effectiveDpr,
        false,
      )
      const radius = Math.abs(transform.scaleX) * shapeExtent * Math.SQRT2 + glowPadding
      const destinationX = Math.max(0, Math.floor(transform.centerX - radius))
      const destinationY = Math.max(0, Math.floor(transform.centerY - radius))
      const maximumX = Math.min(outputWidth, Math.ceil(transform.centerX + radius))
      const maximumY = Math.min(outputHeight, Math.ceil(transform.centerY + radius))
      requiredWidth = Math.max(requiredWidth, maximumX - destinationX)
      requiredHeight = Math.max(requiredHeight, maximumY - destinationY)
    }

    const surface = this.ensureLocalCompositeSurface(
      requiredWidth,
      requiredHeight,
      outputWidth,
      outputHeight,
    )
    const compositeContext = surface?.context ?? null
    if (compositeContext !== null) {
      compositeContext.save()
      compositeContext.globalCompositeOperation = 'source-over'
      compositeContext.fillStyle = fillStyle
      compositeContext.shadowColor = glowStyle
      compositeContext.shadowBlur = glowBlur
    }

    try {
      for (let visibleIndex = 0; visibleIndex < renderCount; visibleIndex += 1) {
        const particleIndex = particles.indices[visibleIndex]
        if (particleIndex === undefined || particleIndex >= particles.sourceCount) continue
        const size = particles.size[particleIndex]
        const x = particles.positionX[particleIndex]
        const y = particles.positionY[particleIndex]
        const rotation = particles.rotation[particleIndex]
        const particleAlpha = particles.alpha[particleIndex]
        if (size === undefined || x === undefined || y === undefined || rotation === undefined || particleAlpha === undefined) continue
        if (!Number.isFinite(size) || !Number.isFinite(x) || !Number.isFinite(y)
          || !Number.isFinite(rotation) || !Number.isFinite(particleAlpha)) continue
        let sampledX = x
        let sampledY = y
        if (motionDistance > 0) {
          if (!writeMotionBlurVector(
            this.motionVector,
            x,
            y,
            particles.previousPositionX?.[particleIndex],
            particles.previousPositionY?.[particleIndex],
            frame.viewport.zoom,
          )) continue
          sampledX += this.motionVector.x * motionDistance
          sampledY += this.motionVector.y * motionDistance
        }
        const finalAlpha = Math.max(0, Math.min(
          1,
          particleAlpha * paint.fadeAlpha * (motionDistance > 0 ? motionAlpha : 1),
        ))
        if (finalAlpha <= 0 || size <= 0) continue

        const transform = writeParticleTransform(
          this.particleTransform,
          sampledX,
          sampledY,
          size,
          rotation,
          frame.project,
          frame.viewport,
          frame.surface.effectiveDpr,
          false,
        )
        const radius = Math.abs(transform.scaleX) * shapeExtent * Math.SQRT2 + glowPadding
        const destinationX = Math.max(0, Math.floor(transform.centerX - radius))
        const destinationY = Math.max(0, Math.floor(transform.centerY - radius))
        const maximumX = Math.min(outputWidth, Math.ceil(transform.centerX + radius))
        const maximumY = Math.min(outputHeight, Math.ceil(transform.centerY + radius))
        const width = maximumX - destinationX
        const height = maximumY - destinationY
        if (compositeContext !== null && surface !== null && width > 0 && height > 0) {
          compositeContext.setTransform(1, 0, 0, 1, 0, 0)
          compositeContext.clearRect(0, 0, width, height)
          const cosine = Math.cos(rotation)
          const sine = Math.sin(rotation)
          compositeContext.setTransform(
            cosine * transform.scaleX,
            sine * transform.scaleX,
            -sine * transform.scaleY,
            cosine * transform.scaleY,
            transform.centerX - destinationX,
            transform.centerY - destinationY,
          )
          compositeContext.globalAlpha = finalAlpha
          this.fillSilhouette(compositeContext, path, custom, paint.shape.builtIn.fillRule)

          this.context.setTransform(1, 0, 0, 1, 0, 0)
          this.context.globalAlpha = 1
          this.context.drawImage(
            surface.image,
            0,
            0,
            width,
            height,
            destinationX,
            destinationY,
            width,
            height,
          )
        }
        this.drawBorder(frame, paint, path, custom, sampledX, sampledY, size, rotation, finalAlpha)
      }
    } finally {
      compositeContext?.restore()
    }
  }

  private setParticleTransform(
    frame: RenderFrameView,
    x: number,
    y: number,
    size: number,
    rotation: number,
    border: boolean,
  ): MutableParticleTransform {
    const transform = writeParticleTransform(
      this.particleTransform,
      x,
      y,
      size,
      rotation,
      frame.project,
      frame.viewport,
      frame.surface.effectiveDpr,
      border,
    )
    const cosine = Math.cos(rotation)
    const sine = Math.sin(rotation)
    this.context.setTransform(
      cosine * transform.scaleX,
      sine * transform.scaleX,
      -sine * transform.scaleY,
      cosine * transform.scaleY,
      transform.centerX,
      transform.centerY,
    )
    return transform
  }

  private fillSilhouette(
    context: Canvas2DContextLike,
    path: CanvasPathLike | null,
    custom: DecodedCanvasSvg | null,
    fillRule: CanvasFillRule,
  ): void {
    if (custom !== null) custom.drawFill(context)
    else if (path !== null) context.fill(path, fillRule)
  }

  private drawGlowFill(
    frame: RenderFrameView,
    paint: ResolvedFramePaint,
    path: CanvasPathLike | null,
    custom: DecodedCanvasSvg | null,
    x: number,
    y: number,
    size: number,
    rotation: number,
    alpha: number,
  ): void {
    this.setParticleTransform(frame, x, y, size, rotation, false)
    this.context.globalAlpha = alpha
    this.fillSilhouette(this.context, path, custom, paint.shape.builtIn.fillRule)
  }

  private drawBorder(
    frame: RenderFrameView,
    paint: ResolvedFramePaint,
    path: CanvasPathLike | null,
    custom: DecodedCanvasSvg | null,
    x: number,
    y: number,
    size: number,
    rotation: number,
    alpha: number,
  ): void {
    if (!paint.showBorder || paint.borderIntensity <= 0) return
    const transform = this.setParticleTransform(frame, x, y, size, rotation, true)
    const borderAlpha = alpha * paint.borderIntensity
    this.context.globalAlpha = borderAlpha
    const denominator = Math.abs(transform.displaySize * frame.viewport.zoom)
    const normalizedBorderWidth = denominator > 0 ? transform.borderWidthCss / denominator : 0
    this.context.lineWidth = normalizedBorderWidth
    if (path !== null) this.context.stroke(path)
    else if (custom !== null) custom.drawBorder(this.context, normalizedBorderWidth)
  }
}

export function createCanvas2DBackend(canvas: HTMLCanvasElement): RenderBackend {
  return new Canvas2DBackend(canvas)
}
